import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import sedan from '@/assets/images/car-park/sedan.webp';
import wagon from '@/assets/images/car-park/wagon.webp';
import suv from '@/assets/images/car-park/suv.webp';
import minivan from '@/assets/images/car-park/minivan.webp';
import minibus from '@/assets/images/car-park/minibus.webp';
import bannerCar from '@/assets/images/car-park/banner-car.webp';
import bannerCarMobile from '@/assets/images/car-park/banner-car-360.webp';

import littleManIcon from '@/assets/icons/car-park/little_man.svg';
import planeIcon from '@/assets/icons/car-park/plane.svg';
import suitcaseIcon from '@/assets/icons/car-park/suitcase.svg';
import sunIcon from '@/assets/icons/car-park/sun.svg';

import { useModal } from '@/hooks/useModal';
import { useScreenType } from '@/hooks/useScreenType';

import { Container } from '@/components/Container';
import { Title } from '@/components/Title';
import { Button } from '@/components/Button';
import { ContainedIcon } from '@/components/ContainedIcon';
import { Carousel } from '@/components/Carousel';

import styles from './CarPark.module.scss';

const CAR_CARDS_IMAGES = [sedan, wagon, suv, minivan, minibus];

const SERVICE_CARDS_ICONS = [
  {
    iconSrc: littleManIcon,
    iconAlt: 'Little Man Icon',
  },
  {
    iconSrc: planeIcon,
    iconAlt: 'Plane Icon',
  },
  {
    iconSrc: suitcaseIcon,
    iconAlt: 'Suitcase Icon',
  },
  {
    iconSrc: sunIcon,
    iconAlt: 'Sun Icon',
  },
];

interface CarCardProps {
  imageSrc: string;
  title: string;
  description: string;
  isVisible?: boolean;
}

function CarCard({ imageSrc, title, description, isVisible }: CarCardProps) {
  return (
    <div className={styles.carCard}>
      <div className={styles.carCard__imageWrapper}>
        <div
          className={clsx(styles.carCard__image, {
            [styles.carCard__image_animate]: isVisible,
          })}
        >
          <img src={imageSrc} alt={title} />
        </div>
      </div>
      <div className={styles.carCard__info}>
        <Title className={styles.carCard__title}>{title}</Title>
        <p className={styles.carCard__description}>{description}</p>
      </div>
    </div>
  );
}

export function CarPark() {
  const { t } = useTranslation('carPark');

  const carCardsLocales = t('carCards', { returnObjects: true }) as {
    title: string;
    description: string;
  }[];
  const carCards = CAR_CARDS_IMAGES.map((image, index) => ({
    image,
    title: carCardsLocales[index].title,
    description: carCardsLocales[index].description,
  }));

  const serviceCardsLocales = t('serviceCards', { returnObjects: true }) as {
    description: string;
  }[];
  const serviceCards = SERVICE_CARDS_ICONS.map((icon, index) => ({
    iconSrc: icon.iconSrc,
    iconAlt: icon.iconAlt,
    description: serviceCardsLocales[index].description,
  }));

  const [isVisible, setIsVisible] = useState(true);
  const sectionRef = useRef<HTMLDivElement | null>(null);

  const { openModal } = useModal();
  const { screenType } = useScreenType();
  const isMobileDevice = screenType === 'tablet' || screenType === 'mobile';

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 },
    );

    const currentSection = sectionRef.current;
    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, []);

  const handleOrderClick = () => {
    openModal('order');
  };

  const handleConsultClick = () => {
    openModal('consult');
  };

  return (
    <section className={styles.carPark} id="car-park" ref={sectionRef}>
      <Container className={styles.carPark__container}>
        {!isMobileDevice && (
          <div className={styles.carPark__carCards}>
            {carCards.map((card, index) => (
              <CarCard
                key={index}
                imageSrc={card.image}
                title={card.title}
                description={card.description}
                isVisible={isVisible}
              />
            ))}
          </div>
        )}
        {isMobileDevice && (
          <Carousel
            id="car-cards-carousel"
            slidesPerView={screenType === 'tablet' ? 2 : 1}
            spaceBetweenSlides={20}
            slides={carCards.map((card, index) => (
              <CarCard
                key={index}
                imageSrc={card.image}
                title={card.title}
                description={card.description}
                isVisible={isVisible}
              />
            ))}
          />
        )}
        <div className={styles.banner}>
          <div className={styles.banner__content}>
            <div className={styles.banner__info}>
              <Title className={styles.banner__title}>{t('banner.title')}</Title>
              <p className={styles.banner__description}>{t('banner.description')}</p>
            </div>
            <div className={styles.banner__buttons}>
              <Button className={styles.banner__button} onClick={handleOrderClick}>
                {t('banner.orderButton')}
              </Button>
              <Button
                className={styles.banner__button}
                color="secondary"
                onClick={handleConsultClick}
              >
                {t('banner.consultButton')}
              </Button>
            </div>
          </div>
          <div className={styles.banner__car}>
            <img
              src={screenType === 'mobile' ? bannerCarMobile : bannerCar}
              alt="Banner Car"
              loading="lazy"
            />
          </div>
        </div>
        {screenType !== 'mobile' && (
          <div className={styles.carPark__serviceCards}>
            {serviceCards.map((card, index) => (
              <div key={index} className={styles.serviceCard}>
                <ContainedIcon
                  className={styles.serviceCard__icon}
                  src={card.iconSrc}
                  alt={card.iconAlt}
                />
                <p className={styles.serviceCard__description}>{card.description}</p>
              </div>
            ))}
          </div>
        )}
        {screenType === 'mobile' && (
          <Carousel
            className={styles.carPark__serviceCardsCarousel}
            id="service-cards-carousel"
            slidesPerView={1}
            spaceBetweenSlides={20}
            slides={serviceCards.map((card, index) => (
              <div key={index} className={styles.serviceCard}>
                <ContainedIcon
                  className={styles.serviceCard__icon}
                  src={card.iconSrc}
                  alt={card.iconAlt}
                />
                <div className={styles.serviceCard__info}>
                  <p className={styles.serviceCard__description}>{card.description}</p>
                </div>
              </div>
            ))}
          />
        )}
      </Container>
    </section>
  );
}
